import React, { useState, useRef, useEffect, useMemo } from "react"
import { 
  FunnelIcon, 
  XMarkIcon, 
  PlusCircleIcon, 
  DocumentDuplicateIcon,
  ClockIcon,
  CheckCircleIcon,
  UserIcon,
  TagIcon,
  CalendarDaysIcon,
  HashtagIcon,
  BoltIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Modal from "aldoo-ra/Modal"

// Common field suggestions with types and icons
const FIELD_SUGGESTIONS = [
  // CMS Content fields
  { key: "content.type", label: "Content Type", type: "text", icon: TagIcon, description: "Content type" },
  { key: "content.status", label: "Status", type: "select", icon: CheckCircleIcon, description: "Status filter", options: ["active", "inactive", "pending", "completed"] },
  { key: "content.category", label: "Category", type: "select", icon: TagIcon, description: "Category filter", options: ["system", "user", "newsletter"] },
  { key: "content.user", label: "User", type: "text", icon: UserIcon, description: "User filter" },
  { key: "content.name", label: "Name", type: "text", icon: HashtagIcon, description: "Name contains" },
  { key: "content.id", label: "Content ID", type: "text", icon: HashtagIcon, description: "Content unique identifier" },
  { key: "content.enabled", label: "Enabled", type: "boolean", icon: BoltIcon, description: "Enabled status" },

  // Common database fields
  { key: "_id", label: "Document ID", type: "text", icon: HashtagIcon, description: "MongoDB document ID" },
  { key: "name", label: "Name", type: "text", icon: HashtagIcon, description: "Name contains" },
  { key: "pid", label: "Product ID", type: "text", icon: HashtagIcon, description: "Product identifier" },
  { key: "type", label: "Type", type: "text", icon: TagIcon, description: "Type field" },
  { key: "enabled", label: "Enabled", type: "boolean", icon: BoltIcon, description: "Enabled status" },
  { key: "deleted", label: "Deleted", type: "boolean", icon: XMarkIcon, description: "Deletion status" },
  { key: "aldooCore", label: "Aldoo Core", type: "boolean", icon: BoltIcon, description: "Aldoo Core content flag" },
  { key: "createdAt", label: "Created", type: "date", icon: CalendarDaysIcon, description: "Creation date" },
  { key: "updatedAt", label: "Updated", type: "date", icon: CalendarDaysIcon, description: "Last updated" },
  { key: "created", label: "Created", type: "date", icon: CalendarDaysIcon, description: "Creation date" },
  { key: "updated", label: "Updated", type: "date", icon: CalendarDaysIcon, description: "Last updated" }
]

// Operators for different field types
const OPERATORS = {
  text: [
    { value: "$regex", label: "Contains", symbol: "~" },
    { value: "$eq", label: "Equals", symbol: "=" },
    { value: "$ne", label: "Not equals", symbol: "≠" },
    { value: "$in", label: "In list", symbol: "∈" }
  ],
  number: [
    { value: "$eq", label: "Equals", symbol: "=" },
    { value: "$ne", label: "Not equals", symbol: "≠" },
    { value: "$gt", label: "Greater than", symbol: ">" },
    { value: "$gte", label: "Greater than or equal", symbol: "≥" },
    { value: "$lt", label: "Less than", symbol: "<" },
    { value: "$lte", label: "Less than or equal", symbol: "≤" },
    { value: "$in", label: "In list", symbol: "∈" }
  ],
  date: [
    { value: "$eq", label: "On date", symbol: "=" },
    { value: "$gt", label: "After", symbol: ">" },
    { value: "$gte", label: "On or after", symbol: "≥" },
    { value: "$lt", label: "Before", symbol: "<" },
    { value: "$lte", label: "On or before", symbol: "≤" }
  ],
  boolean: [
    { value: "$eq", label: "Equals", symbol: "=" }
  ],
  select: [
    { value: "$eq", label: "Equals", symbol: "=" },
    { value: "$ne", label: "Not equals", symbol: "≠" },
    { value: "$in", label: "In list", symbol: "∈" }
  ]
}

// Quick filter presets
const QUICK_FILTERS = [
  { 
    label: "Recent Items", 
    icon: ClockIcon, 
    filters: [{ key: "created", operator: "$gte", value: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] }]
  },
  { 
    label: "Active Only", 
    icon: CheckCircleIcon, 
    filters: [{ key: "status", operator: "$eq", value: "active" }]
  },
  { 
    label: "My Items", 
    icon: UserIcon, 
    filters: [{ key: "user", operator: "$eq", value: "current_user" }]
  }
]

const EnhancedFilterPanel = ({
  value = [],
  onChange,
  onApply,
  onClear,
  className = "",
  disabled = false,
  dataModel = [], // Array of field suggestions specific to current data
  showPreview = true,
  compact = false,
  selectedModelType = null // The currently selected model type to filter relevant fields
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [localValue, setLocalValue] = useState(value)
  const [expandedMode, setExpandedMode] = useState(false)
  const [matchCount, setMatchCount] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})

  // Combine field suggestions with data model and remove duplicates
  const allFieldSuggestions = useMemo(() => {
    const dataModelFields = dataModel.map(field => ({
      key: field.key || field.name,
      label: field.label || field.name,
      type: field.type || "text",
      icon: field.icon || HashtagIcon,
      description: field.description || `${field.label || field.name} field`,
      options: field.options
    }))

    // Determine if we should filter based on model type
    const isCMSModel = selectedModelType?.isCMSModel ?? true // Default to CMS if not specified

    // Filter FIELD_SUGGESTIONS based on model type
    const relevantFieldSuggestions = FIELD_SUGGESTIONS.filter(suggestion => {
      // For CMS models, include content.* fields and common fields
      if (isCMSModel) {
        return suggestion.key.startsWith('content.') ||
               ['_id', 'deleted', 'aldooCore', 'createdAt', 'updatedAt'].includes(suggestion.key)
      }
      // For database models, exclude content.* fields
      return !suggestion.key.startsWith('content.')
    })

    // Combine and deduplicate by key
    const combined = [...relevantFieldSuggestions, ...dataModelFields]
    const seen = new Set()
    const unique = []

    for (const suggestion of combined) {
      if (!seen.has(suggestion.key)) {
        seen.add(suggestion.key)
        unique.push(suggestion)
      }
    }

    return unique
  }, [dataModel, selectedModelType])

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value)
  }, [value])


  const hasActiveFilters = Array.isArray(value) && value.length > 0 && value.some(f => f.key && f.value)
  const hasLocalFilters = Array.isArray(localValue) && localValue.length > 0

  const validateFilter = (filter, index) => {
    const errors = {}
    
    if (!filter.key) {
      errors[`${index}_key`] = "Field is required"
    }
    
    if (!filter.value && filter.value !== false && filter.value !== 0) {
      errors[`${index}_value`] = "Value is required"
    }

    // Type-specific validation
    const fieldSuggestion = allFieldSuggestions.find(s => s.key === filter.key)
    if (fieldSuggestion) {
      if (fieldSuggestion.type === "number" && filter.value && isNaN(Number(filter.value))) {
        errors[`${index}_value`] = "Must be a number"
      }
      if (fieldSuggestion.type === "boolean" && filter.value && !["true", "false"].includes(filter.value.toString().toLowerCase())) {
        errors[`${index}_value`] = "Must be true or false"
      }
    }

    return errors
  }

  const validateAllFilters = () => {
    let allErrors = {}
    localValue.forEach((filter, index) => {
      const filterErrors = validateFilter(filter, index)
      allErrors = { ...allErrors, ...filterErrors }
    })
    setValidationErrors(allErrors)
    return Object.keys(allErrors).length === 0
  }

  const addFilter = (preset = null) => {
    const newFilter = preset || { key: "", operator: "$eq", value: "", type: "text" }
    setLocalValue(prev => {
      const updated = [...prev, newFilter]
      
      // Only auto-apply if this is a preset filter with values
      if (preset && preset.key && preset.value) {
        onChange(updated)
        onApply && onApply(updated)
      }
      
      return updated
    })
  }

  const removeFilter = (index) => {
    setLocalValue(prev => {
      const updated = prev.filter((_, i) => i !== index)
      
      // Auto-apply filters on removal
      onChange(updated)
      onApply && onApply(updated)
      
      return updated
    })
  }

  const updateFilter = (index, field, value) => {
    setLocalValue(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      
      // Auto-set type and operator when key changes
      if (field === "key") {
        const suggestion = allFieldSuggestions.find(s => s.key === value)
        if (suggestion) {
          updated[index].type = suggestion.type
          const defaultOperator = OPERATORS[suggestion.type]?.[0]?.value || "$eq"
          updated[index].operator = defaultOperator
        }
      }
      
      // Auto-apply filters on change
      onChange(updated)
      onApply && onApply(updated)
      
      return updated
    })
  }

  const duplicateFilter = (index) => {
    const filterToDuplicate = { ...localValue[index] }
    setLocalValue(prev => {
      const updated = [...prev, filterToDuplicate]
      
      // Auto-apply if the duplicated filter has valid values
      if (filterToDuplicate.key && filterToDuplicate.value) {
        onChange(updated)
        onApply && onApply(updated)
      }
      
      return updated
    })
  }

  const applyQuickFilter = (quickFilter) => {
    setLocalValue(prev => {
      const updated = [...prev, ...quickFilter.filters]
      
      // Auto-apply quick filters
      onChange(updated)
      onApply && onApply(updated)
      
      return updated
    })
  }


  const handleClear = () => {
    setLocalValue([])
    setValidationErrors({})
    
    // Auto-apply clear action
    onChange([])
    onApply && onApply([])
    
    setIsOpen(false)
    onChange([])
    onClear && onClear()
  }

  // Memoize field suggestions to avoid recreating objects
  const fieldSuggestionOptions = useMemo(() => {
    return allFieldSuggestions.map(suggestion => ({
      value: suggestion.key,
      name: suggestion.label
    }))
  }, [allFieldSuggestions])

  const getOperatorOptions = (fieldType) => {
    const operators = OPERATORS[fieldType] || OPERATORS.text
    return operators.map(op => ({
      value: op.value,
      name: op.label
    }))
  }

  const renderFilterRow = (filter, index) => {
    const fieldSuggestion = allFieldSuggestions.find(s => s.key === filter.key)
    const fieldType = fieldSuggestion?.type || "text"
    const operatorOptions = getOperatorOptions(fieldType)
    const IconComponent = fieldSuggestion?.icon || HashtagIcon

    const hasKeyError = validationErrors[`${index}_key`]
    const hasValueError = validationErrors[`${index}_value`]

    return (
      <div key={index} className="bg-admin_paper border border-admin_bordered rounded-md p-2 space-y-2">
        {/* Compact Header */}
        <div className="flex items-center gap-2">
          <IconComponent className="w-4 h-4 text-admin_text_secondary flex-shrink-0" />

          <Typography
            variant="admin_body"
            className="my-0"
          >
            Filter {index + 1}
          </Typography>

          <div className="flex-grow" />

          {/* Logic (AND/OR) - only show after first filter */}
          {index > 0 && (
            <div className="flex items-center gap-1">
              <Typography variant="admin_txt1_dim" className="my-0 text-xs">
                Logic:
              </Typography>
              <DropdownSelect
                variant="admin"
                options={[
                  { value: "$and", name: "AND", label: "AND" },
                  { value: "$or", name: "OR", label: "OR" }
                ]}
                valueField="value"
                showCheckbox={false}
                value={[{ value: filter.logic || "$and", name: filter.logic === "$or" ? "OR" : "AND" }]}
                onChange={(values) => {
                  const selectedValue = values?.[0]?.value || "$and"
                  updateFilter(index, "logic", selectedValue)
                }}
                showSelectionTick={true}
                fixedWidth="80px"
                className="text-sm"
              />
            </div>
          )}

          <Button
            variant="admin_icon"
            onClick={() => duplicateFilter(index)}
            className="text-admin_text_secondary p-0.5"
            title="Duplicate filter"
          >
            <DocumentDuplicateIcon className="w-4 h-4" />
          </Button>

          <Button
            variant="admin_icon"
            onClick={() => removeFilter(index)}
            className="text-admin_text_secondary hover:text-admin_error p-0.5"
            title="Remove filter"
          >
            <XMarkIcon className="w-5 h-5" />
          </Button>
        </div>

        {/* Layout for Field, Operator, and Value */}
        <div className="grid grid-cols-12 gap-2">
          {/* Field */}
          <div className="col-span-5">
            <Typography variant="admin_txt1_dim">
              Field
            </Typography>

            <DropdownSelect
              variant="admin"
              options={fieldSuggestionOptions}
              valueField="value"
              showCheckbox={false}
              value={fieldSuggestionOptions.filter(opt => opt.value === filter.key)}
              onChange={(values) => {
                const selectedValue = values?.[0]?.value || ""
                updateFilter(index, "key", selectedValue)
              }}
              placeholder="Select field..."
              searchEnabled={true}
              showSelectionTick={true}
              fixedWidth="100%"
              className={`text-sm ${hasKeyError ? "border-admin_error" : ""}`}
            />
          </div>

          {/* Operator */}
          <div className="col-span-3">
            <Typography variant="admin_txt1_dim">
              Operator
            </Typography>

            <DropdownSelect
              variant="admin"
              options={operatorOptions}
              valueField="value"
              showCheckbox={false}
              value={operatorOptions.filter(op => op.value === filter.operator)}
              onChange={(values) => {
                const selectedValue = values?.[0]?.value || "$eq"
                updateFilter(index, "operator", selectedValue)
              }}
              showSelectionTick={true}
              fixedWidth="100%"
              className="text-sm"
            />
          </div>

          {/* Value */}
          <div className="col-span-4">
            <Typography variant="admin_txt1_dim">
              Value
            </Typography>

            {fieldSuggestion?.options ? (
              <DropdownSelect
                variant="admin"
                options={fieldSuggestion.options.map(opt => ({ value: opt, name: opt, label: opt }))}
                valueField="value"
                showCheckbox={false}
                value={fieldSuggestion.options.filter(opt => opt === filter.value).map(opt => ({ value: opt, name: opt, label: opt }))}
                onChange={(values) => {
                  const selectedValue = values?.[0]?.value || ""
                  updateFilter(index, "value", selectedValue)
                }}
                placeholder="Select value..."
                showSelectionTick={true}
                fixedWidth="100%"
                className={`text-sm ${hasValueError ? "border-admin_error" : ""}`}
              />
            ) : (
              <Input
                variant="admin"
                value={filter.value || ""}
                onChange={(e) => updateFilter(index, "value", e.target.value)}
                placeholder={
                  fieldType === "date" ? "YYYY-MM-DD" :
                  fieldType === "boolean" ? "true/false" : 
                  fieldType === "number" ? "123" : 
                  "Enter value..."
                }
                type={fieldType === "number" ? "number" : fieldType === "date" ? "date" : "text"}
                className={`text-sm ${hasValueError ? "border-admin_error" : ""}`}
              />
            )}
          </div>
        </div>

          {/* Error Messages Row (if any) */}
          {(hasKeyError || hasValueError) && (
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-5">
                {hasKeyError && (
                  <Typography variant="admin_caption" className="text-admin_error text-xs">
                    {hasKeyError}
                  </Typography>
                )}
              </div>
              <div className="col-span-3">
                {/* Operator errors would go here if needed */}
              </div>
              <div className="col-span-4">
                {hasValueError && (
                  <Typography variant="admin_caption" className="text-admin_error text-xs">
                    {hasValueError}
                  </Typography>
                )}
              </div>
            </div>
          )}
      </div>
    )
  }

  const activeFilterCount = hasActiveFilters ? value.filter(f => f.key && f.value).length : 0

  return (
    <>
      {/* Filter Button */}
      <div className={`relative ${className}`}>
        <Button
          variant="admin_secondary"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-10 p-0 ${hasActiveFilters ? 'border-admin_primary bg-admin_primary/5' : ''}`}
        >
          <FunnelIcon className={`w-4 h-4 ${hasActiveFilters ? 'text-admin_primary' : ''}`} />
        </Button>
        
        {/* Active Filter Count Badge */}
        {activeFilterCount > 0 && (
          <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-admin_primary text-white rounded-full flex items-center justify-center text-xs font-medium">
            {activeFilterCount}
          </div>
        )}
      </div>

      {/* Modal Drawer */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Advanced Filters"
        variant="admin_drawer_right"
        closeOnClickOutside={false}
        closeOnEscape={false}
        passInteractionEvents={true}
        headerContent={
          showPreview && matchCount !== null && (
            <div className="flex items-center gap-1 text-admin_text_secondary">
              <EyeIcon className="w-4 h-4" />

              <Typography
                variant="admin_body_dim"
                className="my-0"
              >
                {matchCount} matches
              </Typography>
            </div>
          )
        }
        footerContent={
          <div className="flex items-center justify-between p-6">
            <Button
              variant="admin_error"
              onClick={handleClear}
              disabled={!hasLocalFilters}
            >
              Clear All
            </Button>

            <Button
              variant="admin_secondary"
              onClick={() => setIsOpen(false)}
            >
              Close
            </Button>
          </div>
        }
        styleOverrides={{
          overlay: "fixed top-0 left-0 w-full h-full flex justify-end bg-transparent z-50",
          container: "w-full h-full max-w-2xl flex flex-col bg-admin_paper shadow-2xl",
          header: "relative flex justify-between items-center px-6 py-6 border-b border-admin_bordered",
          closeButton: "absolute left-2 top-1/2 -translate-y-1/2 hover:text-admin_text z-10"
        }}
      >
        <div className="space-y-4 p-6">
          {/* Toggle for Expanded Mode */}
          <div className="flex items-center justify-between">
            <Typography variant="admin_label">
              Filters
            </Typography>
            
            <Button
              onClick={() => setExpandedMode(!expandedMode)}
              className="flex items-center justify-center gap-1 text-admin_text_secondary hover:text-admin_text p-1"
              title={expandedMode ? "Hide quick filters" : "Show quick filters"}
            >
              {expandedMode ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}

              <span className="ml-1 text-sm">Quick Filters</span>
            </Button>
          </div>

          {/* Quick Filters */}
          {expandedMode && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {QUICK_FILTERS.map((quickFilter, index) => {
                  const IconComponent = quickFilter.icon
                  return (
                    <Button
                      key={index}
                      variant="admin_secondary"
                      onClick={() => applyQuickFilter(quickFilter)}
                      className="text-sm"
                    >
                      <IconComponent className="w-4 h-4 mr-1" />
                      {quickFilter.label}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="space-y-3">
            {hasLocalFilters ? (
              localValue.map((filter, index) => renderFilterRow(filter, index))
            ) : (
              <div className="text-center py-8 text-admin_text_secondary">
                <FunnelIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />

                <Typography variant="admin_body">
                  No filters added yet
                </Typography>

                <Typography 
                  variant="admin_body_dim" 
                  className="my-0"
                ß>
                  Click "Add Filter" to create your first filter condition
                </Typography>
              </div>
            )}
          </div>

          {/* Add Filter Button */}
          <div className="flex justify-center">
            <Button
              variant="admin_primary"
              onClick={() => addFilter()}
              className="px-6"
            >
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              Add Filter
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default EnhancedFilterPanel